function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import PropTypes from 'prop-types';
import * as ramda from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PopoverBody } from 'reactstrap';
import { v4 } from 'uuid';
import OptionActionsButtonSelectToggle from './optionActionsButtonSelectToggle';
import OptionNestedPickerFieldOptions from '../optionNestedPicker/optionNestedPickerFieldOptions';
import Loader from '../../../common/loader';
import OptionActionsOther from './optionActionsOtherInput';
import { identifierExistsInTree, leafSelectionsToSubtitle, pruneTree, removeFromTree } from '../optionNestedPicker/optionNestedPickerHelpers';
const getIdentifier = option => option.identifier || `_${option.title}`;
const hasChildren = option => option.hasChildren === true || option.options !== undefined || option.children !== undefined;
const OptionActionsButtonBody = ({
  allowsMultipleSelection,
  allowsOther: _allowsOther = false,
  allowsParentSelection,
  deferredOptions: _deferredOptions = null,
  evaluatedOptions,
  evaluateDeferredOptions,
  filterText: _filterText = null,
  handleSearch,
  loadingDeferredOptions,
  options,
  selected,
  selectionTree,
  setFilterText,
  setSelected,
  setSelectionTree,
  toggle
}) => {
  const [optionsToSelect, setOptionsToSelect] = useState([]);
  // update the `selectionTree` and close the Popover upon leaf selection (sometimes)

  const onLeafSelect = useCallback(async option => {
    let tree = JSON.parse(JSON.stringify(selectionTree || {}));
    const isSelected = identifierExistsInTree(tree, option.identifier);
    if (isSelected) {
      tree = removeFromTree(tree, option.identifier);
    } else {
      if (!allowsMultipleSelection) {
        tree = {};
      }
      if (selected.length === 0) {
        tree[option.identifier] = option;
      } else {
        const myTree = tree;
        selected.forEach(selection => {
          if (!tree[selection.identifier]) {
            tree[selection.identifier] = selection;
          }
          if (!tree.children) {
            tree[selection.identifier].children = {};
          }
          tree = tree[selection.identifier].children;
        });
        tree[option.identifier] = option;
        tree = myTree;
      }
    }

    // merge right to allow deselection. If nothing remains selected,
    // return empty object (instead of `null`)
    const newTree = pruneTree(ramda.mergeDeepRight(selectionTree, tree)) || {};
    setSelectionTree(newTree);
    if (!allowsMultipleSelection) {
      // if we don't allowMultipleSelection, close the popover and trigger postActions
      await toggle(newTree);
    }
  }, [allowsMultipleSelection, selected, toggle, selectionTree, setSelectionTree]);

  // handle option selection for different cases
  const onForwardSelect = useCallback(async (option, forceParentSelect = false) => {
    // clear search input
    handleSearch(null);
    if (_deferredOptions) {
      // deferredOptions need re-evaluation if option has children options
      if (hasChildren(option)) {
        await evaluateDeferredOptions(option, null, 0);
      } else {
        await onLeafSelect(option);
        return;
      }
    } else if (!_deferredOptions && (!hasChildren(option) || forceParentSelect)) {
      await onLeafSelect(option);
      if (allowsParentSelection || !forceParentSelect) {
        return;
      }
    }
    const {
      identifier
    } = ramda.last(selected) || {};
    let updatedSelected;
    if (identifier === option.identifier) {
      updatedSelected = ramda.slice(0, -1, selected);
    } else {
      updatedSelected = ramda.append(option, selected);
    }
    setSelected(updatedSelected);
  }, [allowsParentSelection, _deferredOptions, evaluateDeferredOptions, handleSearch, onLeafSelect, selected, setSelected]);

  // turn around and call `onForwardSelect`
  const onSelect = useCallback(async option => {
    setFilterText(null);
    const forceParentSelect = allowsParentSelection;
    onForwardSelect(option, forceParentSelect);
  }, [allowsParentSelection, onForwardSelect, setFilterText]);
  const usableOptions = useMemo(() => {
    // prefer `evaluatedOptions` over `options`
    let optionsToShow = evaluatedOptions && evaluatedOptions.length > 0 ? evaluatedOptions : options;
    selected.forEach(selection => {
      const {
        identifier
      } = selection;
      const foundOption = optionsToShow.find(option => getIdentifier(option) === identifier);
      if (foundOption) {
        optionsToShow = foundOption.options || [];
      }
    });

    /* eslint-disable no-underscore-dangle */
    return optionsToShow.filter(option => getIdentifier(option) !== '_undefined').map((option, index) => _objectSpread(_objectSpread({}, option), {}, {
      _created: index + 1,
      _isSelected: identifierExistsInTree(selectionTree, getIdentifier(option)),
      _crumbs: option.subtitle || leafSelectionsToSubtitle(selectionTree, [...selected, {
        identifier: option.identifier
      }]),
      identifier: getIdentifier(option),
      _entity: option._entity || null
    }));
  }, [evaluatedOptions, options, selected, selectionTree]);
  /* eslint-enable no-underscore-dangle */

  const filteredOptions = useMemo(() => {
    if (_filterText) {
      const reg = new RegExp(_filterText.toLowerCase(), 'i');
      return usableOptions.filter(opt => reg.test(opt.title));
    }
    return usableOptions;
  }, [_filterText, usableOptions]);
  useEffect(() => {
    if (optionsToSelect && optionsToSelect.length > 0) {
      // if there are `optionsSelect` hand them to `onSelect` one at a time, allowing the
      // `selectionTree` state to be accurately updated.
      onSelect(optionsToSelect[0]);
      setOptionsToSelect(optionsToSelect.slice(1));
    }
  }, [onSelect, optionsToSelect, setOptionsToSelect]);
  return /*#__PURE__*/React.createElement(PopoverBody, null, /*#__PURE__*/React.createElement(Loader, {
    loaded: !loadingDeferredOptions
  }, /*#__PURE__*/React.createElement(OptionActionsButtonSelectToggle, {
    allowsMultipleSelection: allowsMultipleSelection,
    setOptionsToSelect: setOptionsToSelect,
    usableOptions: usableOptions
  }), /*#__PURE__*/React.createElement(OptionNestedPickerFieldOptions, {
    showCheck: allowsMultipleSelection,
    options: filteredOptions,
    onSelect: onSelect,
    onForwardSelect: onForwardSelect,
    showIcon: true
  }), _allowsOther ? /*#__PURE__*/React.createElement(OptionActionsOther, {
    onOption: text => onLeafSelect({
      _created: usableOptions.length + 1,
      identifier: v4(),
      title: text
    })
  }) : null));
};
OptionActionsButtonBody.propTypes = {
  allowsOther: PropTypes.bool,
  allowsMultipleSelection: PropTypes.bool.isRequired,
  allowsParentSelection: PropTypes.bool.isRequired,
  deferredOptions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
  evaluatedOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  evaluateDeferredOptions: PropTypes.func.isRequired,
  filterText: PropTypes.string,
  handleSearch: PropTypes.func.isRequired,
  loadingDeferredOptions: PropTypes.bool.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selected: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectionTree: PropTypes.shape({}).isRequired,
  setFilterText: PropTypes.func.isRequired,
  setSelected: PropTypes.func.isRequired,
  setSelectionTree: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired
};
export default OptionActionsButtonBody;